import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Search, Shield, UserX, XCircle } from 'lucide-react';
import { getOrgMembers, removeMember, toggleMember, updateMemberRole } from '../api/api';
import { useAuth } from '../context/AuthContext';
import StyledSelect from '../components/StyledSelect';

const ROLES = ['admin', 'finance_manager', 'accountant', 'project_manager', 'employee', 'viewer'];
const CARD = { borderColor: '#f4d6df', background: '#fff' };
const PINK = '#c44569';

const ROLE_BADGE = {
  organization_head: { bg: '#fce0e8', color: PINK },
  admin:             { bg: '#dbeafe', color: '#1d4ed8' },
  finance_manager:   { bg: '#dcfce7', color: '#15803d' },
  accountant:        { bg: '#fef9c3', color: '#a16207' },
  project_manager:   { bg: '#f3e8ff', color: '#7e22ce' },
  employee:          { bg: '#f1f5f9', color: '#475569' },
  viewer:            { bg: '#fafafa', color: '#64748b' },
};

function RoleBadge({ role }) {
  const style = ROLE_BADGE[role] || ROLE_BADGE.employee;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.08em]"
      style={style}
    >
      {(role || 'employee').replace(/_/g, ' ')}
    </span>
  );
}

export default function OrgAdminMembersPage() {
  const { activeOrg, canManageMembers, canViewUsers, canEditUsers, canSuspendUsers } = useAuth();
  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [query, setQuery]       = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    if (!activeOrg?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await getOrgMembers(activeOrg.id, { page: 1, limit: 500 });
      setMembers(res?.data?.data?.members || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load members.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewUsers) load();
    else setLoading(false);
  }, [activeOrg?.id, canViewUsers]);

  const onRoleChange = async (uid, orgRole) => {
    try {
      await updateMemberRole(activeOrg.id, uid, { org_role: orgRole });
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update role.');
    }
  };

  const onToggle = async (uid, isActive) => {
    try {
      await toggleMember(activeOrg.id, uid, { is_active: !isActive });
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to toggle member status.');
    }
  };

  const onRemove = async (uid) => {
    if (!window.confirm('Remove this member from the organization?')) return;
    try {
      await removeMember(activeOrg.id, uid);
      setSelectedId(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to remove member.');
    }
  };

  const filtered = members.filter((m) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [m.name, m.email, m.org_role].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });

  if (!canViewUsers) {
    return (
      <div className="rounded-3xl border p-6 text-center" style={CARD}>
        <Shield size={32} className="mx-auto mb-3" style={{ color: PINK }} />
        <h2 className="font-bold text-slate-900">Access Restricted</h2>
        <p className="text-sm text-slate-500 mt-1">You do not have permission to view organization members.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-10">
      {/* Header */}
      <div className="rounded-3xl border p-5 shadow-sm" style={CARD}>
        <h1 className="text-xl font-black text-slate-900">Team Members</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage roles, activation status, and membership for {activeOrg?.name || 'your organization'}.
        </p>
      </div>

      {error ? (
        <div className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: '#f9b8ca', background: '#fce0e8', color: PINK }}>
          {error}
        </div>
      ) : null}

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search members by name, email, or role…"
          className="w-full rounded-xl border pl-9 pr-4 py-2.5 text-sm outline-none focus:ring-2"
          style={{ borderColor: '#f4d6df', background: '#fff' }}
        />
      </div>

      {/* Table */}
      <div className="rounded-3xl border overflow-hidden shadow-sm" style={CARD}>
        {loading ? (
          <p className="text-sm text-slate-400 p-6 text-center">Loading members…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 p-6 text-center">No members found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-[0.14em] border-b" style={{ borderColor: '#f4d6df' }}>
                  <th className="py-3 px-5">Member</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Status</th>
                  {canEditUsers || canManageMembers ? <th className="py-3 px-4">Change Role</th> : null}
                  {canSuspendUsers || canManageMembers ? <th className="py-3 px-4">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} className="border-b last:border-0 hover:bg-slate-50 transition" style={{ borderColor: '#f9f0f3' }}>
                    <td className="py-3 px-5">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center font-bold text-sm"
                          style={{ background: 'linear-gradient(135deg,#c44569,#cf6a87)', color: '#fff' }}
                        >
                          {(m.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{m.name}</p>
                          <p className="text-xs text-slate-400 truncate">{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <RoleBadge role={m.org_role} />
                    </td>
                    <td className="py-3 px-4">
                      {Number(m.is_active) ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                          <CheckCircle2 size={13} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-500">
                          <XCircle size={13} /> Inactive
                        </span>
                      )}
                    </td>
                    {canEditUsers || canManageMembers ? (
                      <td className="py-3 px-4">
                        <StyledSelect
                          value={m.org_role}
                          onChange={(val) => onRoleChange(m.id, val)}
                          options={ROLES.map((r) => ({ value: r, label: r.replace(/_/g, ' ') }))}
                          placeholder="Select role"
                        />
                      </td>
                    ) : null}
                    {canSuspendUsers || canManageMembers ? (
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onToggle(m.id, Number(m.is_active))}
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold transition hover:opacity-80"
                            style={Number(m.is_active) ? { background: '#fef9c3', color: '#a16207' } : { background: '#dcfce7', color: '#15803d' }}
                          >
                            {Number(m.is_active) ? 'Deactivate' : 'Activate'}
                          </button>
                          {canManageMembers ? (
                            <button
                              type="button"
                              onClick={() => onRemove(m.id)}
                              className="rounded-lg px-3 py-1.5 text-xs font-semibold transition hover:opacity-80"
                              style={{ background: '#fce0e8', color: PINK }}
                            >
                              Remove
                            </button>
                          ) : null}
                          <Link
                            to={`/org-admin/permissions?userId=${m.id}`}
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold transition hover:opacity-80"
                            style={{ background: '#f3e8ff', color: '#7e22ce' }}
                          >
                            Permissions
                          </Link>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats */}
      {!loading && members.length > 0 ? (
        <div className="rounded-2xl border p-4 text-sm text-slate-500" style={CARD}>
          {members.length} total member{members.length !== 1 ? 's' : ''} ·{' '}
          {members.filter((m) => Number(m.is_active)).length} active ·{' '}
          {members.filter((m) => !Number(m.is_active)).length} inactive
        </div>
      ) : null}
    </div>
  );
}
